// #region Sttedra
import {
  useAxios,
  useDeleteNulls,
  useDev,
  useNormalizer,
} from '@sttedra_ui/framework';
// #endregion

// #region Models
import RegisterApplicationsApiResponseModel from './register-applications.api-response-model';
import RegisterApplicationsConsolidationApiResponseModel from './register-applications-consolidation.api-response-model';
import RegisterApplicationsRequestApiResponseModel from './register-applications-request.api-response-model';
import RegisterPdfApiRequestModel from './register-pdf.api-request-model';
import RegisterUserApiRequestModel from './register-user.api-request-model';
// #endregion

// #region Globals
const {base} = useAxios();
const CONTEXT_BASE_PATH = 'Register';
const {getObjectFilled, getArrayFilled} = useNormalizer();
// #endregion

// #region Public functions
/**
 * Validación de ambos formularios de registro. En esta situación la persona no esta consolidada,
 * ni identificada con clave.
 *
 * @param {Object} registerData - Datos de registro.
 * @param {Value} tokenG - Token reCaptcha del segundo formulario.
 *
 * @returns {Promise<RegisterApplicationsConsolidationApiResponseModel> | Error}
 */
async function preRegister(registerData, temporalToken = null) {
  try {
    if (!registerData) {
      useDev.error('[register.api.js] (preRegister) No se ha recibido información para realizar el registro');
      return {
        registerModel: null,
        response: null,
        error: null,
      };
    }

    const dataRequestModel = new RegisterUserApiRequestModel(registerData);
    useDeleteNulls(dataRequestModel);

    const response = await base.post(`/${CONTEXT_BASE_PATH}/Preregister`,
      dataRequestModel, {
        headers: {
          'Token-Response': temporalToken,
        },
      },
    );

    if (!getObjectFilled(response?.data)) {
      return {
        registerModel: null,
        response: null,
        error: null,
      };
    }

    const registerModel = new RegisterApplicationsRequestApiResponseModel(response.data);
    useDeleteNulls(registerModel );

    return {registerModel, response};
  } catch (error) {
    useDev.error('[register.api.js] (preRegister) Error en la validación de pre-registro', error);
    return {
      registerModel: null,
      response: error?.response || null,
      error,
    };
  }
}

/**
 * Validación de primer formulario de registro.
 *
 * @param {Object} registerData - Datos de registro.
 * @param {Value} tokenG - Token captcha.
 *
 * @returns {Promise<RegisterApplicationsConsolidationApiResponseModel> | Error}
 */
async function validatePreRegister(registerData, tokenG = null) {
  try {
    if (!registerData) {
      useDev.error('[register.api.js] (validatePreRegister) No se ha recibido información para realizar el registro');
      return {
        consolidationModel: null,
        response: null,
        error: null,
      };
    }

    const dataRequestModel = new RegisterUserApiRequestModel(registerData);
    useDeleteNulls(dataRequestModel);

    const response = await base.post(`/${CONTEXT_BASE_PATH}/ValidatePreregister`,
      dataRequestModel, {
        headers: {
          'G-Recaptcha-Response': tokenG || '',
        },
      },
    );

    if (!getObjectFilled(response?.data)) {
      return {
        consolidationModel: null,
        response,
        error: null,
      };
    }

    const consolidationModel = new RegisterApplicationsConsolidationApiResponseModel(response?.data);
    useDeleteNulls(consolidationModel);

    return {consolidationModel, response};
  } catch (error) {
    useDev.error('[register.api.js] (validatePreRegister) Error en la validación de registro', error);
    return {
      consolidationModel: null,
      response: error?.response || null,
      error,
    };
  }
}

/**
 *
 * @param {*} token
 * @returns
 */
async function checkRegisterApplications(token) {
  try {
    const authorization = token ? `Bearer ${token}` : '';
    const response = await base.get(
      `/${CONTEXT_BASE_PATH}/RegisterApplications`,
      {
        headers: {
          Authorization: authorization,
        },
      },
    );

    let data = response.data;
    if (getObjectFilled(data)) {
      data = new RegisterApplicationsApiResponseModel(data);
      useDeleteNulls(data);
    }

    return {
      registerApplicationsModel: data,
      response,
    };
  } catch (error) {
    useDev.error('[register.api.js] checkRegisterApplications', error);
    return {
      consolidationModel: null,
      response: error?.response || null,
      error,
    };
  }
}

async function getRequestByCode({
  code,
}, tokenG) {
  try {
    const response = await base.get(
      `/${CONTEXT_BASE_PATH}/RegisterApplication`,
      {
        params: {
          applicationRegistrationCode: code,
        },
        headers: {
          'G-Recaptcha-Response': tokenG,
        },
      },
    );

    let data = response.data;
    if (getArrayFilled(data)) {
      data = data?.map((u) => new RegisterApplicationsConsolidationApiResponseModel(u));
      useDeleteNulls(data);
    }

    return {
      consolidationModel: data,
      response,
    };
  } catch (error) {
    useDev.error('[register.api.js] getRequestByCode', error);
    return {
      consolidationModel: null,
      response: error?.response || null,
      error,
    };
  }
}

async function getRequestByPulling({
  code,
  identificationNumber,
}, temporalToken = '') {
  try {
    if (!code || !identificationNumber) {
      useDev.log('[register.api.js] getRequestByPulling error params');
      return {
        response: null,
        data: null,
        error: null,
      };
    }

    const response = await base.get(
      `/${CONTEXT_BASE_PATH}/GetRegisterApplicationIdentification`,
      {
        params: {
          applicationRegistrationCode: code,
          identification: identificationNumber?.toUpperCase() || null,
        },
        headers: temporalToken ? {
          'Token-Response': temporalToken,
        } : {},
      },
    );

    let data = response?.data;
    if (getArrayFilled(data)) {
      data = data?.map((u) => new RegisterApplicationsConsolidationApiResponseModel(u));
      useDeleteNulls(data);
    }

    return {
      data,
      error: null,
      response,
    };
  } catch (error) {
    useDev.error('[register.api.js] getRequestByPulling', error);
    return {
      data: null,
      error,
      response: error?.response || null,
    };
  }
}

async function validateCaptcha() {
  try {
    const response = await base.post(`/${CONTEXT_BASE_PATH}/ValidateCaptcha`);
    return {
      data: response?.data ? true : false,
      response,
    };
  } catch (error) {
    return {
      data: true,
      response: error?.response || null,
      error,
    };
  }
}

async function getTypeCaptcha() {
  try {
    const response = await base.get(`/${CONTEXT_BASE_PATH}/TypeCaptcha`);
    return {
      data: response?.data,
      response,
    };
  } catch (error) {
    return {
      data: null,
      response: error?.response || null,
      error,
    };
  }
}

async function generateMitmaCodePdf(data = {}) {
  try {
    const pdfModel = new RegisterPdfApiRequestModel(data);
    useDeleteNulls(pdfModel);

    const response = await base.post(`/${CONTEXT_BASE_PATH}/GenerateMitmaCodePdf`, pdfModel, {
      responseType: 'blob',
    });
    return {
      data: response?.data,
      response,
    };
  } catch (error) {
    return {
      data: null,
      response: error?.response || null,
      error,
    };
  }
}

async function sendMailInfo(identification = '', temporalToken = null) {
  try {
    const response = await base.post(`/${CONTEXT_BASE_PATH}/SendMailInfo`, {
      identification,
    },
    {
      headers: {
        'Token-Response': temporalToken,
      },
    });
    return {
      data: response?.data ? true : false,
      response,
    };
  } catch (error) {
    return {
      data: false,
      response: error?.response || null,
      error,
    };
  }
}

async function updateConsolidateContact(data = {}, temporalToken = null) {
  try {
    const response = await base.post(`/${CONTEXT_BASE_PATH}/UpdateConsolidateContact`, data, {
      headers: {
        'Token-Response': temporalToken,
      },
    });
    return {
      data: response?.data,
      response,
    };
  } catch (error) {
    return {
      data: null,
      response: error?.response || null,
      error,
    };
  }
}

async function getDualYearConfigDates() {
  try {
    const response = await base.get(`/${CONTEXT_BASE_PATH}/DualConfigDates`, {
      timeout: 2000,
    });
    return {
      data: response?.data,
      response,
    };
  } catch (error) {
    return {
      data: null,
      response: error?.response || null,
      error,
    };
  }
}
// #endregion

export {
  checkRegisterApplications,
  generateMitmaCodePdf,
  getRequestByCode,
  getRequestByPulling,
  getTypeCaptcha,
  preRegister,
  sendMailInfo,
  updateConsolidateContact,
  validateCaptcha,
  validatePreRegister,
  getDualYearConfigDates,
};
